import { useState } from 'react';
import { useChain } from '@cosmos-kit/react';
import type { ValidatorInfo, DelegationResponse, Balance, ValidatorRewardInfo } from '@/types/staking';
import { Modal, Button, Input } from '@/components/common';
import { shortenAddress, formatCommission, formatTokenAmount } from '@/utils/formatting';
import { getRedelegationSummary, type RedelegationPlan } from '@/utils/redelegation';
import { simulateAndBroadcast } from '@/services/transactions';
import { useGravityBalance } from '@/hooks/useGravityBalance';
import {
  createDelegateMsg,
  createUndelegateMsg,
  createRedelegateMsg,
  createClaimRewardsMsg,
  toMicroAmount,
} from '@/services/staking-messages';

export type StakingAction = 'delegate' | 'undelegate' | 'redelegate' | 'claim' | 'claim-all' | 'redelegate-from-inactive';

// Component to display rewards breakdown
// Uses the pre-formatted amount_display from the backend for consistent display
function RewardsBreakdown({ rewards, title }: { rewards: Balance[]; title?: string }): JSX.Element | null {
  if (rewards.length === 0) {
    return (
      <p className="text-gray-500 text-sm">No pending rewards</p>
    );
  }

  return (
    <div className="space-y-1">
      {title !== undefined && title !== '' && (
        <p className="text-xs font-medium text-gray-500 mb-2">{title}</p>
      )}
      {rewards.map((r) => (
        <div key={r.denom} className="flex justify-between text-sm">
          <span className="text-gray-600">{r.symbol}</span>
          <span className="font-medium text-gray-900">{r.amount_display}</span>
        </div>
      ))}
    </div>
  );
}

interface StakingModalProps {
  validator?: ValidatorInfo;
  validators?: ValidatorInfo[];
  allActiveValidators?: ValidatorInfo[];
  delegation?: DelegationResponse;
  validatorRewards?: ValidatorRewardInfo;
  /** Pre-aggregated total rewards from the backend (rewards.total) */
  totalRewards?: Balance[];
  redelegationPlan?: RedelegationPlan;
  validatorMap?: Map<string, ValidatorInfo>;
  action: StakingAction;
  onClose: () => void;
}

export function StakingModal({ validator, validators, allActiveValidators, delegation, validatorRewards, totalRewards, redelegationPlan, validatorMap, action, onClose }: StakingModalProps): JSX.Element {
  const { address, getSigningStargateClient } = useChain('gravitybridge');
  const [amount, setAmount] = useState('');
  const [targetValidator, setTargetValidator] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acknowledgeRewardsClaim, setAcknowledgeRewardsClaim] = useState(false);

  // Fetch wallet balance for delegate action
  const { data: walletBalanceUgraviton } = useGravityBalance(address);
  const walletBalanceGrav = walletBalanceUgraviton != null ? (parseFloat(walletBalanceUgraviton) / 1e6).toFixed(6) : '0';

  // Get max amount for undelegate/redelegate from delegation balance
  const maxAmount = delegation ? (parseFloat(delegation.balance.amount) / 1e6).toFixed(6) : '0';

  // Filter out current validator and only include bonded validators (status === 3)
  const availableTargetValidators = (allActiveValidators ?? []).filter(
    v => v.operator_address !== validator?.operator_address && v.status === 3 && !v.jailed
  );

  // Validation: claim-all requires validators array, redelegate-from-inactive requires plan
  if (action === 'claim-all' && (!validators || validators.length === 0)) {
    throw new Error('claim-all action requires validators array');
  }
  if (action === 'redelegate-from-inactive' && (!redelegationPlan || redelegationPlan.operations.length === 0)) {
    throw new Error('redelegate-from-inactive action requires redelegation plan');
  }
  if (action !== 'claim-all' && action !== 'redelegate-from-inactive' && !validator) {
    throw new Error('Single validator actions require validator prop');
  }

  const getTitle = (): string => {
    switch (action) {
      case 'delegate':
        return 'Delegate';
      case 'undelegate':
        return 'Undelegate';
      case 'redelegate':
        return 'Redelegate';
      case 'claim':
        return 'Claim Rewards';
      case 'claim-all':
        return 'Claim All Rewards';
      case 'redelegate-from-inactive':
        return 'Redelegate from Inactive Validators';
      default:
        return '';
    }
  };

  const getDescription = (): string => {
    if (action === 'claim-all') {
      const count = validators?.length ?? 0;
      return `Claim staking rewards from ${count} validator${count !== 1 ? 's' : ''} in a single transaction`;
    }

    if (action === 'redelegate-from-inactive') {
      if (redelegationPlan && validatorMap) {
        return getRedelegationSummary(redelegationPlan, validatorMap);
      }
      return 'Redistribute stake from inactive validators to your active validators';
    }

    const validatorName = validator?.description?.moniker ?? (validator ? shortenAddress(validator.operator_address) : '');
    
    switch (action) {
      case 'delegate':
        return `Delegate tokens to ${validatorName}`;
      case 'undelegate':
        return `Undelegate tokens from ${validatorName}. Tokens will be available after the 21-day unbonding period.`;
      case 'redelegate':
        return `Redelegate tokens from ${validatorName} to another validator`;
      case 'claim':
        return `Claim staking rewards from ${validatorName}`;
      default:
        return '';
    }
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    
    if (address === undefined || address === null || address === '') {
      setError('Please connect your wallet');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Get signing client from cosmos-kit
      const client = await getSigningStargateClient();
      
      if (client === undefined || client === null) {
        throw new Error('Failed to get signing client');
      }

      // Create the appropriate message based on action
      let messages;
      
      switch (action) {
        case 'delegate':
          if (!amount) throw new Error('Amount is required');
          if (!validator) throw new Error('Validator is required');
          messages = [createDelegateMsg(
            address,
            validator.operator_address,
            toMicroAmount(amount)
          )];
          break;
          
        case 'undelegate':
          if (!amount) throw new Error('Amount is required');
          if (!validator) throw new Error('Validator is required');
          messages = [createUndelegateMsg(
            address,
            validator.operator_address,
            toMicroAmount(amount)
          )];
          break;
          
        case 'redelegate':
          if (!amount) throw new Error('Amount is required');
          if (!targetValidator) throw new Error('Target validator is required');
          if (!validator) throw new Error('Validator is required');
          messages = [createRedelegateMsg(
            address,
            validator.operator_address,
            targetValidator,
            toMicroAmount(amount)
          )];
          break;
          
        case 'claim':
          if (!validator) throw new Error('Validator is required');
          messages = [createClaimRewardsMsg(
            address,
            validator.operator_address
          )];
          break;

        case 'claim-all':
          if (!validators || validators.length === 0) {
            throw new Error('No validators to claim from');
          }
          // Create a claim message for each validator
          messages = validators.map(v => createClaimRewardsMsg(
            address,
            v.operator_address
          ));
          break;

        case 'redelegate-from-inactive':
          if (!redelegationPlan || redelegationPlan.operations.length === 0) {
            throw new Error('No redelegation operations');
          }
          // Create a redelegate message for each operation
          messages = redelegationPlan.operations.map(op => createRedelegateMsg(
            address,
            op.from_validator_address,
            op.to_validator_address,
            op.amount
          ));
          break;
          
        default:
          throw new Error('Invalid action');
      }

      // Simulate and broadcast transaction
      const result = await simulateAndBroadcast(
        client,
        address,
        messages,
        { memo: `${action} via Gravity Station` }
      );

      if (result.code !== 0) {
        throw new Error(`Transaction failed: ${result.rawLog}`);
      }

      console.log('Transaction successful:', result.transactionHash);
      
      // Close modal on success
      onClose();
    } catch (err) {
      console.error('Transaction failed:', err);
      const errorMessage = err instanceof Error ? err.message : 'Transaction failed';
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const renderForm = (): JSX.Element => {
    if (action === 'claim') {
      const rewards = validatorRewards?.rewards ?? [];
      return (
        <div className="py-4">
          <p className="text-gray-600 mb-4">
            Claim all available rewards from this validator?
          </p>
          <div className="bg-white border border-gray-200 rounded-md p-3">
            <p className="text-xs font-medium text-gray-500 mb-2">Rewards to claim:</p>
            <RewardsBreakdown rewards={rewards} />
          </div>
        </div>
      );
    }

    if (action === 'claim-all') {
      const validatorCount = validators?.length ?? 0;
      return (
        <div className="py-4">
          <p className="text-gray-600 mb-4">
            Claim all available rewards from {validatorCount} validator{validatorCount !== 1 ? 's' : ''}?
          </p>
          <div className="bg-white border border-gray-200 rounded-md p-3">
            <p className="text-xs font-medium text-gray-500 mb-2">Total rewards to claim:</p>
            <RewardsBreakdown rewards={totalRewards ?? []} />
          </div>
        </div>
      );
    }

    if (action === 'redelegate-from-inactive') {
      const opsCount = redelegationPlan?.operations.length ?? 0;
      const totalGrav = redelegationPlan ? Number(redelegationPlan.total_amount_redelegated) / 1e6 : 0;
      
      return (
        <div className="py-4">
          <p className="text-gray-600 mb-4">
            This will redistribute <strong>{totalGrav.toFixed(2)} GRAV</strong> from inactive validators 
            to your active validators in <strong>{opsCount} operation{opsCount !== 1 ? 's' : ''}</strong>.
          </p>
          <p className="text-sm text-gray-500 mb-4">
            Stake will be distributed evenly across your active validators. All {opsCount} operation{opsCount !== 1 ? 's' : ''} are bundled into a single transaction, so you only need to sign once.
          </p>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={acknowledgeRewardsClaim}
              onChange={(e) => setAcknowledgeRewardsClaim(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">
              I understand redelegating to a new validator claims rewards from the original
            </span>
          </label>
        </div>
      );
    }

    return (
      <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-4">
        <div>
          <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
            Amount (GRAV)
          </label>
          <div className="flex gap-2">
            <Input
              id="amount"
              type="number"
              step="0.000001"
              min="0"
              max={action === 'undelegate' || action === 'redelegate' ? maxAmount : action === 'delegate' ? walletBalanceGrav : undefined}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.0"
              required
            />
            {((action === 'undelegate' || action === 'redelegate') && parseFloat(maxAmount) > 0) && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setAmount(maxAmount)}
              >
                Max
              </Button>
            )}
            {action === 'delegate' && parseFloat(walletBalanceGrav) > 0 && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setAmount(walletBalanceGrav)}
              >
                Max
              </Button>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            {(action === 'undelegate' || action === 'redelegate') && parseFloat(maxAmount) > 0
              ? `Available: ${maxAmount} GRAV`
              : action === 'delegate' && parseFloat(walletBalanceGrav) > 0
              ? `Available: ${walletBalanceGrav} GRAV`
              : 'Enter the amount of GRAV tokens'}
          </p>
        </div>

        {action === 'undelegate' && (
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={acknowledgeRewardsClaim}
              onChange={(e) => setAcknowledgeRewardsClaim(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">
              I understand this claims pending rewards
            </span>
          </label>
        )}

        {action === 'redelegate' && (
          <>
            <div>
              <label htmlFor="target-validator" className="block text-sm font-medium text-gray-700 mb-1">
                Target Validator
              </label>
              {availableTargetValidators.length > 0 ? (
                <select
                  id="target-validator"
                  value={targetValidator}
                  onChange={(e) => setTargetValidator(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  required
                >
                  <option value="">Select a validator...</option>
                  {availableTargetValidators.map((v) => (
                    <option key={v.operator_address} value={v.operator_address}>
                      {v.description?.moniker ?? shortenAddress(v.operator_address)} ({formatCommission(v.commission)})
                    </option>
                  ))}
                </select>
              ) : (
                <Input
                  id="target-validator"
                  type="text"
                  value={targetValidator}
                  onChange={(e) => setTargetValidator(e.target.value)}
                  placeholder="gravityvaloper..."
                  required
                />
              )}
              <p className="mt-1 text-xs text-gray-500">
                Select the validator to redelegate to
              </p>
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={acknowledgeRewardsClaim}
                onChange={(e) => setAcknowledgeRewardsClaim(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">
                I understand redelegating to a new validator claims rewards from the original
              </span>
            </label>
          </>
        )}
      </form>
    );
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={getTitle()}
    >
      <div className="space-y-6">
        <div>
          <p className="text-sm text-gray-600">{getDescription()}</p>
        </div>

        <div className="bg-gray-50 p-4 rounded-md">
          <div className="text-sm">
            {action === 'claim-all' && validators ? (
              // For claim-all, show list of validators
              <div>
                <div className="mb-2">
                  <span className="text-gray-700 font-medium">Validators ({validators.length}):</span>
                </div>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {validators.map((v, idx) => (
                    <div key={v.operator_address} className="text-gray-900">
                      {idx + 1}. {v.description?.moniker ?? shortenAddress(v.operator_address)}
                    </div>
                  ))}
                </div>
              </div>
            ) : action === 'redelegate-from-inactive' && redelegationPlan && validatorMap ? (
              // For redelegate-from-inactive, show source and target validators
              <div className="space-y-4">
                <div>
                  <div className="mb-2">
                    <span className="text-gray-700 font-medium">From Inactive Validators:</span>
                  </div>
                  <div className="max-h-20 overflow-y-auto space-y-1 pl-2">
                    {redelegationPlan.source_validators.map((addr) => {
                      const v = validatorMap.get(addr);
                      return (
                        <div key={addr} className="text-red-700">
                          • {v?.description?.moniker ?? shortenAddress(addr)}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <div className="mb-2">
                    <span className="text-gray-700 font-medium">To Active Validators:</span>
                  </div>
                  <div className="max-h-20 overflow-y-auto space-y-1 pl-2">
                    {redelegationPlan.target_validators.map((addr) => {
                      const v = validatorMap.get(addr);
                      return (
                        <div key={addr} className="text-green-700">
                          • {v?.description?.moniker ?? shortenAddress(addr)}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : validator ? (
              // For single validator actions, show details
              <>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-700 font-medium">Validator:</span>
                  <span className="font-semibold text-gray-900">
                    {validator.description?.moniker ?? shortenAddress(validator.operator_address)}
                  </span>
                </div>
                {validator.commission !== undefined && validator.commission !== null && validator.commission !== '' && (
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-700 font-medium">Commission:</span>
                    <span className="font-semibold text-gray-900">{formatCommission(validator.commission)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-700 font-medium">Voting Power:</span>
                  <span className="font-semibold text-gray-900">{formatTokenAmount(validator.tokens)} GRAV</span>
                </div>
              </>
            ) : null}
          </div>
        </div>

        {renderForm()}

        {error !== null && error !== '' && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="flex gap-3 justify-end pt-4 border-t">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={(e) => { void handleSubmit(e); }}
            disabled={
              isProcessing ||
              ((action !== 'claim' && action !== 'claim-all' && action !== 'redelegate-from-inactive') && (amount === undefined || amount === null || amount === '')) ||
              ((action === 'undelegate' || action === 'redelegate' || action === 'redelegate-from-inactive') && !acknowledgeRewardsClaim)
            }
          >
            {isProcessing ? 'Processing...' : 'Confirm'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
